// src/pages/OurDrivers.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../styles/ourDrivers.css";

const OurDrivers = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "CheapCabGrab Licensed Drivers",
    description:
      "DBS-checked, council-licensed professional private hire drivers specialising in Heathrow and airport transfers.",
    provider: {
      "@type": "Organization",
      name: "CheapCabGrab",
      url: "https://www.cheapcabgrab.com",
    },
    areaServed: ["Heathrow Airport", "London", "Slough", "Windsor", "Maidenhead"],
  };

  return (
    <div className="drivers-page">

      {/* SEO */}
      <Helmet>
        <title>CheapCabGrab Drivers – DBS Checked, Licensed & Trained</title>
        <meta
          name="description"
          content="Meet CheapCabGrab’s fully licensed, DBS-checked professional drivers. Trained in airport transfers, disability awareness, safeguarding, and customer care."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/our-drivers" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* HERO */}
      <header className="drivers-hero">
        <h1>👮‍♂️ CheapCabGrab Drivers</h1>
        <p>
          Professional. Licensed. DBS-checked. Trained for airport transfers,
          customer care and safe transport. You’re always in trusted hands.
        </p>

        <div className="drivers-hero-buttons">
          <Link to="/" className="drivers-primary-btn">🚖 Get a Quote</Link>
          <Link to="/driver" className="drivers-secondary-btn">📋 Driver Sign-Up</Link>
        </div>
      </header>

      {/* OVERVIEW */}
      <section className="drivers-section" id="overview">
        <h2>⭐ What makes CheapCabGrab drivers different?</h2>
        <p>
          We work only with <strong>licensed, professional private hire drivers</strong>.
          Every driver completes strict checks, training and compliance to ensure
          the highest standard of safety, comfort and reliability.
        </p>

        <div className="drivers-feature-grid">
          <div className="drivers-feature-card">
            <h3>🔐 Enhanced DBS Checked</h3>
            <p>Every driver holds an Enhanced DBS certificate and is enrolled in the DBS Update Service.</p>
          </div>

          <div className="drivers-feature-card">
            <h3>🛂 Council Licensed</h3>
            <p>All drivers are vetted, tested and licensed by their local council (e.g. Slough Borough Council).</p>
          </div>

          <div className="drivers-feature-card">
            <h3>✈️ Airport-Specialist</h3>
            <p>Our team handles Heathrow, Gatwick, Luton and Stansted daily with flight tracking and timing knowledge.</p>
          </div>

          <div className="drivers-feature-card">
            <h3>♿ Disability Awareness</h3>
            <p>Drivers complete disability awareness training to support passengers with mobility or access needs.</p>
          </div>

          <div className="drivers-feature-card">
            <h3>🛡 Safeguarding Training</h3>
            <p>All drivers undergo safeguarding training focusing on vulnerable passengers and safe conduct.</p>
          </div>

          <div className="drivers-feature-card">
            <h3>👥 Diversity & Inclusion</h3>
            <p>Trained to provide respectful, inclusive service to all passengers.</p>
          </div>

          <div className="drivers-feature-card">
            <h3>🚫 No Surge Pricing</h3>
            <p>CheapCabGrab never applies surge pricing. Fares remain fixed and clear.</p>
          </div>

          <div className="drivers-feature-card">
            <h3>🌙 24/7 Availability</h3>
            <p>Airport-specialist drivers available day and night with reliable arrival and timing.</p>
          </div>
        </div>
      </section>

      {/* SLOUGH LICENSING */}
      <section className="drivers-section" id="licensing">
        <h2>📜 Licensing & Requirements</h2>
        <p>Every CheapCabGrab driver meets or exceeds strict UK licensing rules.</p>

        <div className="drivers-two-col">
          <div>
            <h3>General Requirements</h3>
            <ul className="drivers-list">
              <li>✔ Minimum age: <strong>21 years</strong></li>
              <li>✔ Held UK licence for at least <strong>2 years</strong></li>
              <li>✔ Right to work in the UK</li>
	      <li>✔ Maximum of <strong>6 penalty points</strong></li>
              <li>✔ Must meet <strong>DVLA Group 2 medical standards</strong></li>
            </ul>

            <h3>Training & Testing</h3>
            <ul className="drivers-list">
              <li>✔ Enhanced DBS certificate + Update Service</li>
              <li>✔ Local council competency test</li>
              <li>✔ Safeguarding course</li>
              <li>✔ Disability awareness training</li>
              <li>✔ Practical driving assessment</li>
              <li>✔ English language test</li>
              <li>✔ Medical exam with GP</li>
            </ul>
          </div>

          <div>
            <div className="drivers-info-box">
              <h3>Why this matters</h3>
              <p>
                Airport passengers trust CheapCabGrab because our drivers pass
                some of the strictest licensing processes in the UK.
              </p>
            </div>

            <div className="drivers-info-box">
              <h3>Become a driver</h3>
              <p>
                If you are interested in joining our licensed team, visit our{" "}
                <Link to="/driver">Driver Onboarding</Link> page.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CUSTOMER EXPERIENCE */}
      <section className="drivers-section" id="experience">
        <h2>💼 Professional conduct</h2>

        <div className="drivers-two-col">
          <div>
            <ul className="drivers-list">
              <li>✔ On-time pickups with live tracking</li>
              <li>✔ Help with luggage</li>
              <li>✔ Clean and well-maintained vehicles</li>
              <li>✔ Clear route planning</li>
              <li>✔ Respectful, friendly and professional</li>
            </ul>
          </div>

          <div className="drivers-info-box">
            <h3>Customer Ratings</h3>
            <p>
              Passengers regularly rate CheapCabGrab drivers for punctuality,
              driving quality and overall service.
            </p>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="drivers-section" id="faq">
        <h2>❓ Driver FAQs</h2>

        <div className="drivers-faq-grid">

          <div className="drivers-faq-card">
            <h3>Do CheapCabGrab drivers track flights?</h3>
            <p>Yes — for most airlines we monitor delays and adjust pickup times accordingly.</p>
          </div>

          <div className="drivers-faq-card">
            <h3>Is Meet & Greet included?</h3>
            <p>It's optional. There is a small extra charge for inside-terminal Meet & Greet.</p>
          </div>

          <div className="drivers-faq-card">
            <h3>Do drivers wait if flights are delayed?</h3>
            <p>Yes. Drivers adjust their arrival time based on real-time tracking.</p>
          </div>

          <div className="drivers-faq-card">
            <h3>Are drivers allowed to assist passengers?</h3>
            <p>Yes — drivers can assist with luggage and meet passengers at agreed locations.</p>
          </div>

          <div className="drivers-faq-card">
            <h3>How are drivers vetted?</h3>
            <p>Each driver is DBS-checked, licensed, trained and medically approved.</p>
          </div>

          <div className="drivers-faq-card">
            <h3>Can drivers make multiple pickups?</h3>
            <p>Yes, this can be arranged — contact support for multi-stop routes.</p>
          </div>

        </div>

        <Link to="/faq" className="drivers-inline-link">See full FAQs →</Link>
      </section>

      {/* CTA */}
      <section className="drivers-cta">
        <h2>Ready to book with a trusted CheapCabGrab driver?</h2>
        <p>Your safe, licensed airport transfer is one click away.</p>

        <div className="drivers-cta-buttons">
          <Link to="/" className="drivers-primary-btn">🚖 Get a Quote</Link>
          <Link to="/contact" className="drivers-secondary-btn">📩 Contact Support</Link>
          <Link to="/driver" className="drivers-secondary-btn">👤 Join as a Driver</Link>
        </div>
      </section>

    </div>
  );
};

export default OurDrivers;

